import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import CallHistory from "./CallHistory";
import RecentCall from "./RecentCall";
import { useAuth } from "../../context/AuthContext";

const CallCenter = () => {
  const { user } = useAuth();
  const wsRef = useRef(null);
  const retryCountRef = useRef(0);
  const retryTimeoutRef = useRef(null);
  const maxRetriesRef = useRef(20); // Maximum number of retry attempts
  const pingIntervalRef = useRef(null);
  const lastPongRef = useRef(null);
  const healthCheckIntervalRef = useRef(null);

  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [rawData, setRawData] = useState([]);
  const [recent, setRecent] = useState([]);
  const [allActive, setAllActive] = useState([]);
  const [query, setQuery] = useState("");
  const [online, setOnline] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [finished, setFinished] = useState([]);
  const [missed, setMissed] = useState([]);
  const [visits, setVisits] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState("disconnected"); // "connected", "disconnected", "connecting", "error"
  const [selectedTab, setSelectedTab] = useState("Online Now");

  const connectToWs = useCallback(() => {
    // Clear any existing retry timeout and ping interval
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }

    // const ws = new WebSocket("wss://callcenter-wscall.teech.tv");
    const ws = new WebSocket("wss://callcenter-euwscall.teech.tv:3006");
    wsRef.current = ws;

    // Connection timeout (10 seconds)
    const connectionTimeout = setTimeout(() => {
      if (ws.readyState !== WebSocket.OPEN) {
        console.log("Connection timeout - closing WebSocket");
        ws.close();
      }
    }, 10000);

    ws.onopen = () => {
      console.log("WebSocket connected successfully");
      setConnectionStatus("connected");
      // Clear connection timeout
      clearTimeout(connectionTimeout);
      // Reset retry count on successful connection
      retryCountRef.current = 0;

      // Start ping interval to keep connection alive and detect stale connections
      lastPongRef.current = Date.now();
      pingIntervalRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "ping" }));
        }
      }, 30000); // Send ping every 30 seconds

      // Start health check interval to detect stale connections
      healthCheckIntervalRef.current = setInterval(() => {
        if (lastPongRef.current && Date.now() - lastPongRef.current > 60000) {
          console.log(
            "Connection appears stale - no pong received in 60 seconds, reconnecting",
          );
          setConnectionStatus("error");
          if (wsRef.current) {
            wsRef.current.close();
          }
        }
      }, 10000); // Check every 10 seconds

      if (user) {
        ws.send(
          JSON.stringify({
            type: "init",
            agent: {
              agentId: user.agentId,
              name: user.name,
              group:
                user.groups && user.groups.length > 0 ? user.groups[0] : null,
            },
          }),
        );
      }
    };

    ws.onmessage = (event) => {
      const parsed = JSON.parse(event.data);
      switch (parsed.type) {
        case "init":
          setOnline(
            parsed.calls.filter(
              (call) =>
                (call.state === "ringing" ||
                  call.state === "started" ||
                  call.state === "active") &&
                call.group !== "test",
            ),
          );
          break;
        case "call_deleted":
          setOnline((prevOnline) =>
            prevOnline.filter((call) => call.callId !== parsed.callId),
          );
          break;

        case "call":
          if (
            (parsed.data.state === "active" ||
              parsed.data.state === "ringing" ||
              parsed.data.state === "started") &&
            parsed.data.group !== "test" &&
            parsed.data.group &&
            user.groups.includes(parsed.data.group)
          ) {
            setOnline((prevOnline) => {
              const existingCallIndex = prevOnline.findIndex(
                (call) => call.callId === parsed.data.callId,
              );
              if (existingCallIndex !== -1) {
                // Replace existing call
                const updatedOnline = [...prevOnline];
                updatedOnline[existingCallIndex] = parsed.data;
                return updatedOnline;
              } else {
                // Add new call
                return [...prevOnline, parsed.data];
              }
            });
          } else {
            setOnline((prevOnline) =>
              prevOnline.filter((call) => call.callId !== parsed.data.callId),
            );
          }
          break;
        case "pong":
          console.log("Pong received - connection is healthy");
          lastPongRef.current = Date.now();
          break;
        default:
          console.log("Unknown message type");
          break;
      }
    };

    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
      setConnectionStatus("error");
      // Don't retry immediately on error - wait for onclose
    };

    ws.onclose = (event) => {
      console.log("WebSocket connection closed:", event.code, event.reason);
      setConnectionStatus("disconnected");

      // Clear connection timeout
      clearTimeout(connectionTimeout);
      // Clear ping interval
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      // Clear health check interval
      if (healthCheckIntervalRef.current) {
        clearInterval(healthCheckIntervalRef.current);
        healthCheckIntervalRef.current = null;
      }

      // Only retry if the connection was not closed intentionally and user exists
      if (event.code !== 1000 && user) {
        // Check if we've exceeded maximum retry attempts
        if (retryCountRef.current >= maxRetriesRef.current) {
          console.error("Maximum WebSocket retry attempts reached. Giving up.");
          return;
        }

        // Calculate exponential backoff delay with max of 2 minutes (120 seconds)
        const maxDelay = 120000; // 2 minutes in milliseconds
        const baseDelay = Math.min(
          1000 * Math.pow(2, retryCountRef.current),
          maxDelay,
        );

        // Add jitter to avoid thundering herd
        const jitter = Math.random() * 1000;
        const delay = baseDelay + jitter;

        console.log(
          `Scheduling WebSocket retry in ${Math.round(delay / 1000)} seconds (attempt ${retryCountRef.current + 1}/${maxRetriesRef.current})`,
        );

        retryTimeoutRef.current = setTimeout(() => {
          retryCountRef.current++;
          connectToWs();
        }, delay);
      } else {
        // Reset retry count for intentional closures
        retryCountRef.current = 0;
      }
    };
  }, [user]);

  const fetchAll = useCallback(async () => {
    try {
      let isTestFilter = false;
      let group = null;

      if (user && user.groups && user.groups.length > 0) {
        group = user.groups[0];
      }

      // Build the API URL with query parameter if needed
      let apiUrl = "https://callcenter.teech.tv/api/v2/calls";

      if (
        user &&
        (user.agentId === "agent-001" || user.agentId === "agent-002")
      ) {
        // console.log(user.agentId);
      } else {
        if (isTestFilter && !group) {
          apiUrl += "?type=test";
        }

        if (!isTestFilter && group) {
          apiUrl += `?group=${group}`;
        }

        if (isTestFilter && group) {
          apiUrl += `?type=test&group=${group}`;
        }
      }

      const { data } = await axios.get(apiUrl);
      const active = data.activeCalls.filter((item) => item.agentId);
      setAllActive(data.activeCalls);
      const allCalls = [...active, ...data.recentCalls].sort(
        (a, b) => b.startTime - a.startTime,
      );

      const _allCalls = allCalls.filter((item) => item.group !== "test");
      const endedActive = data.activeCalls.filter((item) => item.endTime);

      setRawData(_allCalls);

      const missedCalls = data.recentCalls.filter((item) => !item.agentId);
      const missedSorted = [...missedCalls, ...endedActive].sort(
        (a, b) => b.startTime - a.startTime,
      );

      const _missed = missedSorted.filter((item) => item.group !== "test");
      setRecent(_missed.slice(0, 10));

      const _recent = _allCalls.filter(
        (item) =>
          item.clientName &&
          item.clientName.toLowerCase().includes(query.toLowerCase()),
      );
      setRecent(_recent);

      const finishedCalls = data.finishedCalls.filter(
        (item) => item.group !== "test" && item.endTime,
      );
      setFinished(finishedCalls);

      const _missedCalls = data.missedCalls.filter(
        (item) => item.group !== "test" && item.endTime,
      );
      setMissed(_missedCalls);

      // Create a Set of callIds from finishedCalls and missedCalls to avoid duplicates
      const existingCallIds = new Set([
        ...finishedCalls.map((call) => call.callId),
        ..._missedCalls.map((call) => call.callId),
      ]);

      let visitsCalls = data.recentCalls.filter(
        (item) =>
          item.group !== "test" &&
          item.endTime &&
          !existingCallIds.has(item.callId),
      );

      setVisits(visitsCalls);
    } catch (error) {
      console.error("Error fetching live users:", error);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      setConnectionStatus("connecting");
      connectToWs();
    }

    // Listen for online/offline events to handle network connectivity changes
    const handleOnline = () => {
      console.log("Network connection restored");
      if (
        user &&
        (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN)
      ) {
        // Reset retry count and attempt immediate reconnection
        retryCountRef.current = 0;
        connectToWs();
      }
    };

    const handleOffline = () => {
      console.log("Network connection lost");
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      // Cleanup on unmount
      if (wsRef.current) {
        wsRef.current.close(1000, "Component unmounting");
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      if (healthCheckIntervalRef.current) {
        clearInterval(healthCheckIntervalRef.current);
      }
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [user, connectToWs]);

  // Add connection status indicator to the UI
  const getConnectionStatusColor = () => {
    switch (connectionStatus) {
      case "connected":
        return "#4CAF50"; // Green
      case "connecting":
        return "#FF9800"; // Orange
      case "disconnected":
      case "error":
        return "#F44336"; // Red
      default:
        return "#9E9E9E"; // Gray
    }
  };

  const getConnectionStatusText = () => {
    switch (connectionStatus) {
      case "connected":
        return "Connected";
      case "connecting":
        return "Connecting...";
      case "disconnected":
        return "Disconnected";
      case "error":
        return "Connection Error";
      default:
        return "Unknown";
    }
  };

  useEffect(() => {
    let isFetching = false;

    const fetchData = async () => {
      if (!isFetching) {
        isFetching = true;
        await fetchAll();
        isFetching = false;
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 30000);

    return () => clearInterval(interval);
  }, [fetchAll]);

  useEffect(() => {
    if (notificationsEnabled) {
      const notification = document.getElementById("ringing");
      if (
        online &&
        online.some(
          (call) =>
            call.state === "ringing" && call.call_direction === "incoming",
        )
      ) {
        if (notification) {
          notification.play().catch((error) => {
            console.error("Failed to play notification:", error);
          });
        }
      } else {
        if (notification) {
          notification.pause();
        }
      }
    }
  }, [online, notificationsEnabled]);

  return (
    <main className="call-center">
      <div className="call-center__section">
        <div className="call-center__section__title">
          Incoming Calls
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              marginLeft: "auto",
              position: "relative",
            }}
          >
            <div className="call-center__status">
              <div
                className="call-center__status__indicator"
                style={{
                  backgroundColor: getConnectionStatusColor(),
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  display: "inline-block",
                  marginRight: "6px",
                }}
              />
            </div>
            <div className="call-center__notifications">
              <div className="call-center__notifications__title">
                Notifications
              </div>
              <div
                className={`call-center__notifications__switcher ${!notificationsEnabled ? "call-center__notifications__switcher--disabled" : ""}`}
                onClick={() => setNotificationsEnabled(!notificationsEnabled)}
              >
                <div className="call-center__notifications__switcher__text">
                  {notificationsEnabled ? "ON" : "OFF"}
                </div>
                <div className="call-center__notifications__switcher__indicator" />
              </div>
            </div>
          </div>
        </div>
        <div className="call-center__section__list" style={{ border: "none" }}>
          <CallHistory
            incoming={true}
            items={online.filter(
              (call) =>
                call.state === "ringing" && call.call_direction !== "outgoing",
            )}
            allActive={allActive}
          />
        </div>
      </div>
      <div className="call-center__section">
        <div className="call-center__section__title">
          CALL LOGS
          <div className="call-center__filter__search">
            <div className="call-center__filter__search__icon"></div>
            <input
              type="text"
              placeholder="Search by URL, client ID, or name..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="call-center__search__input"
            />
          </div>
        </div>
        <div className="call-center__filter">
          <div className="call-center__filter__tabs">
            <div
              className={`call-center__filter__tabs__item ${selectedTab === "Online Now" ? "active" : ""}`}
              onClick={() => setSelectedTab("Online Now")}
            >
              Online Users
            </div>
            <div
              className={`call-center__filter__tabs__item ${selectedTab === "Recent" ? "active" : ""}`}
              onClick={() => setSelectedTab("Recent")}
            >
              Ended Calls
            </div>
            <div
              className={`call-center__filter__tabs__item ${selectedTab === "Missed" ? "active" : ""}`}
              onClick={() => setSelectedTab("Missed")}
            >
              Missed Calls
            </div>
            <div
              className={`call-center__filter__tabs__item ${selectedTab === "Visits" ? "active" : ""}`}
              onClick={() => setSelectedTab("Visits")}
            >
              Visits
            </div>
          </div>
        </div>
        <div className="call-center__section__list">
          {selectedTab === "Online Now" && (
            <CallHistory
              incoming={false}
              onlineNow={true}
              items={online
                .filter(
                  (call) =>
                    call.state !== "ringing" &&
                    ((call.user_source &&
                      call.user_source
                        .toLowerCase()
                        .includes(query.toLowerCase())) ||
                      (call.clientId &&
                        call.clientId
                          .toLowerCase()
                          .includes(query.toLowerCase())) ||
                      (call.clientName &&
                        call.clientName
                          .toLowerCase()
                          .includes(query.toLowerCase()))),
                )
                .reverse()}
              allActive={allActive}
            />
          )}
          {selectedTab === "Recent" && (
            <RecentCall
              items={finished.filter(
                (item) =>
                  (item.user_source &&
                    item.user_source
                      .toLowerCase()
                      .includes(query.toLowerCase())) ||
                  (item.clientId &&
                    item.clientId
                      .toLowerCase()
                      .includes(query.toLowerCase())) ||
                  (item.clientName &&
                    item.clientName
                      .toLowerCase()
                      .includes(query.toLowerCase())),
              )}
              missed={false}
              visits={false}
              recents={true}
            />
          )}
          {selectedTab === "Missed" && (
            <RecentCall
              items={missed.filter(
                (item) =>
                  ((item.agentId && item.call_direction === "outgoing") ||
                    !item.agentId) &&
                  ((item.user_source &&
                    item.user_source
                      .toLowerCase()
                      .includes(query.toLowerCase())) ||
                    (item.clientId &&
                      item.clientId
                        .toLowerCase()
                        .includes(query.toLowerCase())) ||
                    (item.clientName &&
                      item.clientName
                        .toLowerCase()
                        .includes(query.toLowerCase()))),
              )}
              missed={true}
              visits={false}
            />
          )}
          {selectedTab === "Visits" && (
            <RecentCall
              items={visits.filter(
                (item) =>
                  !item.agentId &&
                  ((item.user_source &&
                    item.user_source
                      .toLowerCase()
                      .includes(query.toLowerCase())) ||
                    (item.clientId &&
                      item.clientId
                        .toLowerCase()
                        .includes(query.toLowerCase())) ||
                    (item.clientName &&
                      item.clientName
                        .toLowerCase()
                        .includes(query.toLowerCase()))),
              )}
              missed={false}
              visits={true}
            />
          )}
        </div>
      </div>
    </main>
  );
};

export default CallCenter;
